import { useCallback, useRef } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  type AdminAvailabilityRestrictionPayload,
  deleteEventRegistration,
  generateAdminPaymentLink,
  markEventRegistrationPaid,
  rejectEventRegistration,
  type AdminEventRegistration,
  updateEventRegistrationAvailabilityRestriction,
} from "../services/eventRegistrationAdminService";

type PaymentMethod = "pix" | "credit_card" | "debit_card";

type UseAdminRegistrationActionsInput = {
  adminPaymentMethod: PaymentMethod;
  isAdmin: boolean;
  loadChallengeEventRegistrations: (eventId?: string | null) => Promise<void>;
  loadEventRegistrations: () => Promise<void>;
  manualRegistrationEventIdForChallenge: string;
  removeRegistrationFromChallengeSelection: (
    registration?: AdminEventRegistration | null,
  ) => void;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  token?: string | null;
};

export function useAdminRegistrationActions({
  adminPaymentMethod,
  isAdmin,
  loadChallengeEventRegistrations,
  loadEventRegistrations,
  manualRegistrationEventIdForChallenge,
  removeRegistrationFromChallengeSelection,
  showErrorToast,
  showSuccessToast,
  token,
}: UseAdminRegistrationActionsInput) {
  const t = siteTexts.admin;
  const paymentLinkInFlightRef = useRef<Set<number>>(new Set());

  const reloadRegistrations = useCallback(async () => {
    await loadEventRegistrations();
    await loadChallengeEventRegistrations(manualRegistrationEventIdForChallenge);
  }, [
    loadChallengeEventRegistrations,
    loadEventRegistrations,
    manualRegistrationEventIdForChallenge,
  ]);

  const onMarkPaidRegistration = useCallback(
    async (id: number, _registration?: AdminEventRegistration | null) => {
      if (!token || !isAdmin) return;
      try {
        await markEventRegistrationPaid(token, id);
        await reloadRegistrations();
        showSuccessToast(t.eventLink.toasts.paid);
      } catch (_err) {
        showErrorToast(t.eventLink.toasts.paidError);
      }
    },
    [
      isAdmin,
      reloadRegistrations,
      showErrorToast,
      showSuccessToast,
      t.eventLink.toasts.paid,
      t.eventLink.toasts.paidError,
      token,
    ],
  );

  const onCopyPaymentLink = useCallback(
    async (
      id: number,
      paymentMethod: PaymentMethod = adminPaymentMethod,
      existingPaymentLink?: string | null,
    ) => {
      if (!token) return;
      const registrationId = Number(id || 0);
      if (!Number.isFinite(registrationId) || registrationId <= 0) return;
      if (paymentLinkInFlightRef.current.has(registrationId)) return;
      paymentLinkInFlightRef.current.add(registrationId);
      const existingLink = String(existingPaymentLink || "").trim();
      try {
        if (existingLink) {
          await navigator.clipboard
            .writeText(existingLink)
            .catch(() => Promise.resolve());
          showSuccessToast(t.eventLink.toasts.paymentLinkCopied);
          return;
        }
        const response = await generateAdminPaymentLink(token, registrationId, {
          paymentMethod,
        });
        await reloadRegistrations();
        if (response.paymentUrl) {
          await navigator.clipboard
            .writeText(response.paymentUrl)
            .catch(() => Promise.resolve());
          showSuccessToast(t.eventLink.toasts.paymentLinkCopied);
        } else if (response.alreadyPaid) {
          showSuccessToast(t.eventLink.toasts.paymentAlreadyConfirmed);
        } else {
          showErrorToast(t.eventLink.toasts.noPaymentLink);
        }
      } catch (err) {
        showErrorToast(
          err instanceof Error ? err.message : t.eventLink.toasts.copyError,
        );
      } finally {
        paymentLinkInFlightRef.current.delete(registrationId);
      }
    },
    [
      adminPaymentMethod,
      reloadRegistrations,
      showErrorToast,
      showSuccessToast,
      t.eventLink.toasts.copyError,
      t.eventLink.toasts.noPaymentLink,
      t.eventLink.toasts.paymentAlreadyConfirmed,
      t.eventLink.toasts.paymentLinkCopied,
      token,
    ],
  );

  const onRejectRegistration = useCallback(
    async (id: number, registration?: AdminEventRegistration | null) => {
      if (!token) return;
      try {
        await rejectEventRegistration(token, id);
        removeRegistrationFromChallengeSelection(registration);
        await reloadRegistrations();
        showSuccessToast(t.eventLink.toasts.rejected);
      } catch (err) {
        showErrorToast(
          err instanceof Error ? err.message : t.eventLink.toasts.rejectError,
        );
      }
    },
    [
      reloadRegistrations,
      removeRegistrationFromChallengeSelection,
      showErrorToast,
      showSuccessToast,
      t.eventLink.toasts.rejectError,
      t.eventLink.toasts.rejected,
      token,
    ],
  );

  const onDeleteRegistration = useCallback(
    async (id: number, registration?: AdminEventRegistration | null) => {
      if (!token) return;
      try {
        await deleteEventRegistration(token, id);
        removeRegistrationFromChallengeSelection(registration);
        await reloadRegistrations();
        showSuccessToast(t.eventLink.toasts.registrationRemoved);
      } catch (err) {
        showErrorToast(
          err instanceof Error
            ? err.message
            : t.eventLink.toasts.registrationRemoveError,
        );
      }
    },
    [
      reloadRegistrations,
      removeRegistrationFromChallengeSelection,
      showErrorToast,
      showSuccessToast,
      t.eventLink.toasts.registrationRemoveError,
      t.eventLink.toasts.registrationRemoved,
      token,
    ],
  );

  const onSaveAvailabilityRestriction = useCallback(
    async (id: number, payload: AdminAvailabilityRestrictionPayload) => {
      if (!token) return;
      try {
        const response = await updateEventRegistrationAvailabilityRestriction(
          token,
          id,
          payload,
        );
        if (payload.mode === "impact") {
          return {
            impactedCount: response.impact?.impactedCount || 0,
            lockedCount: response.impact?.lockedCount || 0,
          };
        }
        await reloadRegistrations();
        showSuccessToast("Restrição de disponibilidade salva.");
        return {
          impactedCount: response.impact?.impactedCount || 0,
          lockedCount: response.impact?.lockedCount || 0,
        };
      } catch (err) {
        showErrorToast(
          err instanceof Error
            ? err.message
            : "Não foi possível salvar a restrição.",
        );
        throw err;
      }
    },
    [reloadRegistrations, showErrorToast, showSuccessToast, token],
  );

  return {
    onCopyPaymentLink,
    onDeleteRegistration,
    onMarkPaidRegistration,
    onSaveAvailabilityRestriction,
    onRejectRegistration,
  };
}
